import React, { createContext, useState, useContext, ReactNode } from "react";

interface BusinessInfo {
  businessId: string;
  businessDomain: string;
  businessName: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  businessInfo: BusinessInfo | null;
  login: (
    accessToken: string,
    refreshToken: string,
    businessInfo?: BusinessInfo,
  ) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  // Check if authentication tokens exist in localStorage
  const checkAuthTokens = () => {
    const accessToken = localStorage.getItem("access_token");
    const refreshToken = localStorage.getItem("refresh_token");
    return (
      accessToken && refreshToken && accessToken !== "" && refreshToken !== ""
    );
  };

  // Initialize state based on localStorage value
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    return (
      localStorage.getItem("isAuthenticated") === "true" && checkAuthTokens()
    );
  });

  const [businessInfo, setBusinessInfo] = useState<BusinessInfo | null>(() => {
    const stored = localStorage.getItem("business_info");
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return null;
      }
    }
    return null;
  });

  const login = (
    accessToken: string,
    refreshToken: string,
    businessInfo?: BusinessInfo,
  ) => {
    setIsAuthenticated(true);
    localStorage.setItem("isAuthenticated", "true");
    localStorage.setItem("access_token", accessToken);
    localStorage.setItem("refresh_token", refreshToken);

    if (businessInfo) {
      setBusinessInfo(businessInfo);
      localStorage.setItem("business_info", JSON.stringify(businessInfo));
    }
  };

  const logout = () => {
    setIsAuthenticated(false);
    setBusinessInfo(null);
    localStorage.setItem("isAuthenticated", "false");
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("business_info");
  };

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, businessInfo, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
